import {
  GridRowId,
  GridRowSelectionModel,
  GridSortDirection,
} from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { ListInvoiceActionKind, ListInvoiceActions } from "./action";
import { isBrazilDealer, isMexicoDealer } from "./selector";
import { DealerAccount } from "../../../../api/dealer/types";
import {
  InvoiceHeaderDetailsV2,
  InvoicesResponse,
} from "../../../../api/invoices/interfaces";
import { UserDetails } from "../../../../context/UserContext/UserContext";
import {
  FilterType,
  FilterWithInfo,
  InvoicesFilterState,
  InvoiceTypeEnum,
  SelectedInvoices,
} from "../invoicesGrid/types";

const SORT_ORDER_DEFAULT = "desc" as GridSortDirection;
const SORTBY_DEFAULT_FIELD = "invoiceNumber";

export function getInitialFilters(
  currentDate: dayjs.Dayjs,
  dealerLocation?: string,
  enableD2DOrderInvoice = false
): InvoicesFilterState {
  return {
    filters: {
      invoiceNumber: "",
      invoiceType: "",
      srcProcessType: "",
      invoiceTypeList: [
        { key: InvoiceTypeEnum.ALL, value: "" },
        { key: InvoiceTypeEnum.OrderInvoice, value: "Order Invoice" },
        { key: InvoiceTypeEnum.CreditMemo, value: "Credit Memo" },
        { key: InvoiceTypeEnum.DebitMemo, value: "Debit Memo" },
        ...(enableD2DOrderInvoice
          ? [
              {
                key: InvoiceTypeEnum.D2DOrderInvoice,
                value: "D2D Order Invoice",
              },
            ]
          : []),

        ...(isBrazilDealer(dealerLocation)
          ? [
              {
                key: InvoiceTypeEnum.DailyReturnCredit,
                value: "Daily Return Credit",
              },
            ]
          : []),

        ...(isMexicoDealer(dealerLocation)
          ? [
              {
                key: InvoiceTypeEnum.SurplusReturnCredit,
                value: "Surplus Return Credit",
              },
            ]
          : []),
      ],
      nfeNumber: "",
      documentNumber: "",
      handlingUnitNumber: "",
      dealerReference: "",
      officialDocumentNumber: "",
      partNumber: "",
      factura: "",
      startDate: currentDate.subtract(30, "days"),
      endDate: currentDate,
      minDate: currentDate.add(1, "day").subtract(12, "month"),
    },
    pageNumber: 0,
    pageSize: 0,
    sortField: SORTBY_DEFAULT_FIELD,
    sortDir: SORT_ORDER_DEFAULT,
  };
}

export const getInitialState = (
  currentDate: dayjs.Dayjs,
  language: string,
  featureToggles: {
    ENABLE_HU_FOR_INVOICES: boolean;
    ENABLE_INVOICE_LEADING_ZEROS: boolean;
    enableCreateReturns: boolean;
    enableClaims: boolean;
    enableClaimsByHU: boolean;
    ENABLE_CLAIMS_CHINA: boolean;
    ENABLE_CLAIMS_INDIA: boolean;
    showRedirectTOJDPointLegacy: boolean;
    enableVendorDirectReturn: boolean;
    enableInvoiceConsolidation: boolean;
    enableMultipleAccounts: boolean;
    enableCorporateViewDpaRole: boolean;
    enableD2DOrderInvoice?: boolean;
  },
  isRBACEnabled: boolean,
  isCorporateViewEnabled: boolean,
  rbacAccess: {
    returns: { createEnabled: boolean };
    handlingUnits: { viewEnabled: boolean };
    claims: { viewEnabled: boolean; createEnabled: boolean };
  },
  selectedAccounts: DealerAccount[],
  dealerAccount?: DealerAccount,
  dealerLocation?: string,
  authState?: { isAuthenticated?: boolean; accessToken?: string; xid?: string },
  user?: UserDetails,
  enableInvoiceTypeExportDisplay?: boolean
): ListInvoiceState => ({
  invoicesResponse: {
    invoices: [],
    pageSize: 0,
    pageNumber: 0,
    total: 0,
    totalViewed: 0,
  },
  filter: getInitialFilters(
    currentDate,
    dealerLocation,
    featureToggles.enableD2DOrderInvoice
  ),
  accessToken: authState,
  language,
  page: 0,
  pageSize: 10,
  featureToggles: {
    enableHUForInvoices: featureToggles.ENABLE_HU_FOR_INVOICES,
    enableInvoiceLeadingZeros: featureToggles.ENABLE_INVOICE_LEADING_ZEROS,
    enableCreateReturns: featureToggles.enableCreateReturns,
    enableClaims: featureToggles.enableClaims,
    enableClaimsByHU: featureToggles.enableClaimsByHU,
    enableClaimsChina: featureToggles.ENABLE_CLAIMS_CHINA,
    enableClaimsIndia: featureToggles.ENABLE_CLAIMS_INDIA,
    showRedirectTOJDPointLegacy: featureToggles.showRedirectTOJDPointLegacy,
    enableVendorDirectReturn: featureToggles.enableVendorDirectReturn,
    enableInvoiceConsolidation: featureToggles.enableInvoiceConsolidation,
    enableMultipleAccounts: featureToggles.enableMultipleAccounts,
    enableCorporateViewDpaRole: featureToggles.enableCorporateViewDpaRole,
    enableD2DOrderInvoice: featureToggles.enableD2DOrderInvoice ?? false,
  },
  isRBACEnabled,
  isCorporateViewEnabled,
  rbacAccess,
  selectedAccounts,
  dealerAccount,
  dealerLocation,
  user,
  isLoading: false,
  selectedRowIds: [],
  selectedInvoices: [],
  includedInvoicesAllNew: [],
  rowSelectionModel: [],
  selectAll: false,
  selectAllNew: false,
  numberOfFilterChanges: 1,
  actions: {
    isLoading: false,
  },
  isInvoicesLoaded: false,
  downloadingNFeNumber: "",
  consolidatedInvoices: {
    include: [],
    exclude: [],
  },
  enableInvoiceTypeExportDisplay,
});

export interface ListInvoiceState {
  invoicesResponse: InvoicesResponse;
  filter: InvoicesFilterState;
  accessToken?: {
    isAuthenticated?: boolean;
    accessToken?: string;
    xid?: string;
  };
  language: string;
  page: number;
  pageSize: number;
  featureToggles: {
    enableHUForInvoices: boolean;
    enableInvoiceLeadingZeros: boolean;
    enableCreateReturns: boolean;
    enableClaims: boolean;
    enableClaimsByHU: boolean;
    enableClaimsChina: boolean;
    enableClaimsIndia: boolean;
    showRedirectTOJDPointLegacy: boolean;
    enableVendorDirectReturn: boolean;
    enableInvoiceConsolidation: boolean;
    enableMultipleAccounts: boolean;
    enableCorporateViewDpaRole: boolean;
    enableD2DOrderInvoice: boolean;
  };
  isRBACEnabled: boolean;
  isCorporateViewEnabled: boolean;
  rbacAccess: {
    returns: { createEnabled: boolean };
    handlingUnits: { viewEnabled: boolean };
    claims: { viewEnabled: boolean; createEnabled: boolean };
  };
  selectedAccounts: DealerAccount[];
  dealerAccount?: DealerAccount;
  dealerLocation?: string;
  user?: any;
  isLoading: boolean;
  selectedRowIds: string[];
  selectedInvoices: SelectedInvoices[];
  includedInvoicesAllNew: SelectedInvoices[];
  rowSelectionModel: GridRowSelectionModel;
  selectAll: boolean;
  selectAllNew: boolean;
  numberOfFilterChanges: number;
  actions: {
    isLoading: boolean;
    invoicesDetails?: InvoiceHeaderDetailsV2;
  };
  error?: any;
  isInvoicesLoaded: boolean;
  downloadingNFeNumber: string;
  consolidatedInvoices: {
    include: string[];
    exclude: string[];
  };
  enableInvoiceTypeExportDisplay?: boolean;
}

const totalPageCount = (state: ListInvoiceState) =>
  Math.ceil(state.invoicesResponse.total / state.pageSize);

const setFilter = (
  filterState: InvoicesFilterState,
  value: string,
  type: string
): FilterWithInfo => {
  const updatedValue = value.replace(/[[\]]/g, ""); // to prevent search API failure
  switch (type) {
    case FilterType.InvoiceNumber:
      return {
        ...filterState.filters,
        invoiceNumber: updatedValue,
      };
    case FilterType.InvoiceType:
      return {
        ...filterState.filters,
        invoiceType: updatedValue,
      };
    case FilterType.SourceProcessType:
      return {
        ...filterState.filters,
        srcProcessType: updatedValue,
      };
    case FilterType.NotaFiscalNumber:
      return {
        ...filterState.filters,
        nfeNumber: updatedValue.toUpperCase(),
      };
    case FilterType.DocumentNumber:
      return {
        ...filterState.filters,
        documentNumber: updatedValue.toUpperCase(),
      };
    case FilterType.HandlingUnitNumber:
      return {
        ...filterState.filters,
        handlingUnitNumber: updatedValue.toUpperCase(),
      };
    case FilterType.DealerReference:
      return {
        ...filterState.filters,
        dealerReference: updatedValue,
      };
    case FilterType.OfficialDocumentNumber:
      return {
        ...filterState.filters,
        officialDocumentNumber: updatedValue,
      };
    case FilterType.PartNumber:
      return {
        ...filterState.filters,
        partNumber: updatedValue.toUpperCase(),
      };
    case FilterType.DealerAccount:
      return {
        ...filterState.filters,
        dealerId: updatedValue,
      };
    case FilterType.Factura:
      return {
        ...filterState.filters,
        factura: updatedValue,
      };
    default:
      return filterState.filters;
  }
};

const cleanFilters = (filterState: InvoicesFilterState): FilterWithInfo => {
  const { filters } = filterState;
  const fieldsToTrim = [
    "invoiceNumber",
    "nfeNumber",
    "documentNumber",
    "officialDocumentNumber",
    "handlingUnitNumber",
    "dealerReference",
    "partNumber",
    "factura",
  ] as const;

  const trimmedFilters = Object.fromEntries(
    fieldsToTrim.map((field) => [field, getTrimmedValue(filters[field] || "")])
  );

  return {
    ...filters,
    ...trimmedFilters,
  };
};

const getTrimmedValue = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue || null;
};

export const listInvoiceReducer = (
  state: ListInvoiceState,
  action: ListInvoiceActions
): ListInvoiceState => {
  switch (action.type) {
    case ListInvoiceActionKind.SET_INVOICES: {
      let { rowSelectionModel } = state;
      const { invoices } = action.payload;

      if (state.selectAll) {
        rowSelectionModel = invoices
          .filter(
            (invoice) =>
              !state.consolidatedInvoices.exclude.includes(
                invoice.invoiceNumber
              )
          )
          .map((invoice) => invoice.invoiceNumber);
      } else if (state.selectAllNew) {
        rowSelectionModel = [
          ...new Set(
            [
              ...invoices.filter(
                (invoice) =>
                  invoice.viewedBy == null &&
                  invoice.viewedDate == null &&
                  !state.consolidatedInvoices.exclude.includes(
                    invoice.invoiceNumber
                  )
              ),
              ...invoices.filter((invoice) =>
                state.consolidatedInvoices.include.includes(
                  invoice.invoiceNumber
                )
              ),
            ].map((invoice) => invoice.invoiceNumber)
          ),
        ];
      }
      return {
        ...state,
        invoicesResponse: {
          ...action.payload,
          invoices,
        },
        isInvoicesLoaded: true,
        rowSelectionModel,
      };
    }
    case ListInvoiceActionKind.SET_IS_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ListInvoiceActionKind.PREVIOUS_PAGE:
      return {
        ...state,
        page: state.page === 0 ? state.page : state.page - 1,
      };
    case ListInvoiceActionKind.NEXT_PAGE:
      return {
        ...state,
        page:
          state.page === totalPageCount(state) - 1
            ? state.page
            : state.page + 1,
      };
    case ListInvoiceActionKind.FIRST_PAGE:
      return {
        ...state,
        page: 0,
      };
    case ListInvoiceActionKind.LAST_PAGE:
      return {
        ...state,
        page: totalPageCount(state) - 1,
      };
    case ListInvoiceActionKind.SET_PAGE_ROWS_SIZE:
      return {
        ...state,
        pageSize: action.payload,
        page: 0,
      };
    case ListInvoiceActionKind.SET_FILTER: {
      const updatedFilter = setFilter(
        state.filter,
        action.payload.value,
        action.payload.type
      );
      return {
        ...state,
        filter: {
          ...state.filter,
          filters: updatedFilter,
        },
      };
    }
    case ListInvoiceActionKind.CLEAR_FILTER: {
      const updatedFilter = setFilter(state.filter, "", action.payload);
      return {
        ...state,
        filter: {
          ...state.filter,
          filters: updatedFilter,
        },
      };
    }
    case ListInvoiceActionKind.CLEAN_FILTERS: {
      const updatedFilter = cleanFilters(state.filter);
      return {
        ...state,
        filter: {
          ...state.filter,
          filters: updatedFilter,
        },
      };
    }
    case ListInvoiceActionKind.SET_DATE_RANGE_FILTER_ACTION:
      return {
        ...state,
        filter: {
          ...state.filter,
          filters: {
            ...state.filter.filters,
            startDate: action.payload.startDate,
            endDate: action.payload.endDate,
          },
        },
      };
    case ListInvoiceActionKind.SET_SORT_PARAMS_ACTION: {
      const { field, sort } = action.payload;
      let sortingField: string | undefined;
      switch (field) {
        case "invoiceDate":
          sortingField = "billingDate";
          break;
        case "__tree_data_group__":
          sortingField = "invoiceNumber";
          break;
        case "invoiceNumber":
        case "viewedDate":
        case "viewedBy":
        case "nfeNumber":
        case "officialDocumentNumber":
        case "dealerAccount":
          sortingField = field;
          break;
        default:
          break;
      }
      if (
        state.filter.sortDir === sort &&
        state.filter.sortField === sortingField
      )
        return state;
      return {
        ...state,
        filter: {
          ...state.filter,
          sortField: sortingField,
          sortDir: sort,
        },
      };
    }
    case ListInvoiceActionKind.SET_ROW_SELECTION_MODEL: {
      return {
        ...state,
        rowSelectionModel: action.payload,
      };
    }
    case ListInvoiceActionKind.HEADER_SELECT_CHANGE:
      return {
        ...state,
        selectAll: false,
        selectAllNew: false,
      };
    case ListInvoiceActionKind.ROW_SELECT_CHANGE: {
      const consolidatedInvoices = getConsolidatedInvoices(
        state,
        action.payload.rowId,
        action.payload.value
      );
      return {
        ...state,
        consolidatedInvoices,
      };
    }
    case ListInvoiceActionKind.SET_SELECT_ALL:
      if (action.payload) {
        return {
          ...state,
          selectAll: action.payload,
          selectAllNew: false,
          rowSelectionModel: state.invoicesResponse.invoices.map(
            (invoice) => invoice.invoiceNumber
          ),
          consolidatedInvoices: {
            include: [],
            exclude: [],
          },
        };
      }
      return {
        ...state,
        selectAll: action.payload,
        rowSelectionModel: [],
        consolidatedInvoices: {
          include: [],
          exclude: [],
        },
      };
    case ListInvoiceActionKind.SET_SELECT_ALL_NEW:
      if (action.payload) {
        return {
          ...state,
          selectAllNew: action.payload,
          selectAll: false,
          rowSelectionModel: state.invoicesResponse.invoices
            .filter(
              (invoice) =>
                invoice.viewedBy == null && invoice.viewedDate == null
            )
            .map((invoice) => invoice.invoiceNumber),
          consolidatedInvoices: {
            include: [],
            exclude: [],
          },
        };
      }
      return {
        ...state,
        selectAllNew: action.payload,
        rowSelectionModel: [],
        consolidatedInvoices: {
          include: [],
          exclude: [],
        },
      };
    case ListInvoiceActionKind.SET_IS_ACTIONS_LOADING:
      if (action.payload) {
        return {
          ...state,
          actions: {
            ...state.actions,
            isLoading: action.payload,
            invoicesDetails: undefined,
          },
        };
      }
      return {
        ...state,
        actions: {
          ...state.actions,
          isLoading: action.payload,
        },
      };
    case ListInvoiceActionKind.FETCH_INVOICE_DETAILS_COMPLETE:
      return {
        ...state,
        actions: {
          ...state.actions,
          invoicesDetails: action.payload,
        },
      };
    case ListInvoiceActionKind.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case ListInvoiceActionKind.DOWNLOADING_NFE_NUMBER:
      return {
        ...state,
        downloadingNFeNumber: action.payload,
      };
    case ListInvoiceActionKind.SET_D2D_ORDER_INVOICE_ENABLED: {
      const { invoiceTypeList } = getInitialFilters(
        dayjs(),
        state.dealerLocation,
        action.payload
      ).filters;
      const invoiceType =
        action.payload ||
        state.filter.filters.invoiceType !== "D2D Order Invoice"
          ? state.filter.filters.invoiceType
          : "";

      return {
        ...state,
        featureToggles: {
          ...state.featureToggles,
          enableD2DOrderInvoice: action.payload,
        },
        filter: {
          ...state.filter,
          filters: {
            ...state.filter.filters,
            invoiceType,
            invoiceTypeList,
          },
        },
      };
    }
    case ListInvoiceActionKind.UPDATE_SELECTED_ACCOUNTS:
      return {
        ...state,
        selectedAccounts: action.payload,
        invoicesResponse: {
          invoices: [],
          pageSize: 0,
          pageNumber: 0,
          total: 0,
          totalViewed: 0,
        },
        isInvoicesLoaded: false,
        filter: getInitialFilters(
          dayjs(),
          state.dealerLocation,
          state.featureToggles.enableD2DOrderInvoice
        ),
      };
    case ListInvoiceActionKind.UPDATE_DEALER_ACCOUNT:
      return {
        ...state,
        dealerAccount: action.payload,
        invoicesResponse: {
          invoices: [],
          pageSize: 0,
          pageNumber: 0,
          total: 0,
          totalViewed: 0,
        },
        isInvoicesLoaded: false,
        filter: getInitialFilters(
          dayjs(),
          state.dealerLocation,
          state.featureToggles.enableD2DOrderInvoice
        ),
      };
    case ListInvoiceActionKind.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload,
      };
    default:
      return state;
  }
};

function getConsolidatedInvoices(
  state: ListInvoiceState,
  rowId: GridRowId,
  isChecked: boolean
) {
  const { include, exclude } = state.consolidatedInvoices;
  if (state.selectAll) {
    return {
      include,
      exclude: isChecked
        ? exclude.filter((id) => id !== rowId)
        : Array.from(new Set([...exclude, rowId.toString()])),
    };
  }
  if (state.selectAllNew) {
    const invoiceRow = state.invoicesResponse.invoices.find(
      (invoice) => invoice.invoiceNumber === rowId
    );
    const isNewInvoice =
      invoiceRow?.viewedBy == null && invoiceRow?.viewedDate == null;
    if (isNewInvoice) {
      return {
        include,
        exclude: isChecked
          ? exclude.filter((id) => id !== rowId)
          : Array.from(new Set([...exclude, rowId.toString()])),
      };
    }
    return {
      include: isChecked
        ? Array.from(new Set([...include, rowId.toString()]))
        : include.filter((id) => id !== rowId),
      exclude,
    };
  }
  return { include, exclude };
}
